import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText } from 'lucide-react';
import CollapsibleSection from '../ui/CollapsibleSection';
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { markdownRenderers } from '../utils/markdownRenderers';
import { formatDate } from '../utils/dateFormatter';
import useApi from '../api';
import { toast } from 'react-toastify';
import SectionHeader from '@/components/ui/SectionHeader';

interface Report {
    id: string;
    related_report_id: string;
    report_status: string;
    report: string;
    created_at: string;
    updated_at: string;
    conversation_id: string;
    visitor_id?: string;
    lead_id?: string;
    report_name?: string;
}

interface ReportsProps {
    // Either pass reports directly OR a leadId to fetch them
    reports?: Report[];
    leadId?: string;
    onReportDeleted?: () => void;
    compact?: boolean; // When true, use smaller headers for session details view
    variant?: 'card' | 'panel'; // 'panel' renders as a borderless collapsible section
    sectionStateKey?: string; // localStorage prefix for collapse persistence (panel variant)
    contactDetail?: boolean; // match the full contact page's section-heading treatment
}

const Reports: React.FC<ReportsProps> = ({ reports: propReports, leadId, onReportDeleted, compact = false, variant = 'card', sectionStateKey, contactDetail = false }) => {
    const [reports, setReports] = useState<Report[]>(propReports || []);
    const [loading, setLoading] = useState(false);
    const [selectedReport, setSelectedReport] = useState<Report | null>(null);
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [deleteReportLoading, setDeleteReportLoading] = useState(false);
    const [deleteReportDialogOpen, setDeleteReportDialogOpen] = useState(false);
    const { remove, get } = useApi();

    // Fetch reports if leadId is provided
    useEffect(() => {
        if (leadId) {
            fetchReports();
        }
    }, [leadId]);

    // Update reports when prop changes
    useEffect(() => {
        if (propReports) {
            setReports(propReports);
        }
    }, [propReports]);

    const fetchReports = async () => {
        if (!leadId) return;
        
        setLoading(true);
        try {
            const response = await get(`/leads/${leadId}/reports`);
            setReports(response.data.data || []);
        } catch (error) {
            console.error('Error fetching reports:', error);
            toast.error('Failed to load reports');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteReport = async () => {
        if (!selectedReport) return;

        setDeleteReportLoading(true);
        try {
            await remove(`/reports/${selectedReport.id}`);
            toast.success('Report deleted successfully');
            setReportDialogOpen(false);
            setDeleteReportDialogOpen(false);
            
            // Refresh reports
            if (leadId) {
                fetchReports();
            } else if (onReportDeleted) {
                onReportDeleted();
            }
        } catch (error) {
            console.error('Error deleting report:', error);
            toast.error('Failed to delete report. Please try again.');
        } finally {
            setDeleteReportLoading(false);
        }
    };

    if (loading) {
        if (contactDetail) {
            return (
                <div className="space-y-2">
                    <SectionHeader title="Reports" icon={FileText} />
                    <div className="rounded-xl border bg-card overflow-hidden animate-pulse">
                        <div className="divide-y">
                            {Array.from({ length: 2 }).map((_, i) => (
                                <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-4">
                                    <div className="h-4 w-36 bg-muted rounded" />
                                    <div className="h-5 w-16 bg-muted rounded-full" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div className="rounded-xl border bg-card overflow-hidden animate-pulse">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                    <div className="h-4 w-16 bg-muted rounded" />
                    <div className="h-4 w-4 bg-muted rounded" />
                </div>
                <div className="divide-y">
                    {Array.from({ length: 2 }).map((_, i) => (
                        <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-4">
                            <div className="space-y-1.5 flex-1">
                                <div className="h-4 w-36 bg-muted rounded" />
                                <div className="h-3 w-24 bg-muted rounded" />
                            </div>
                            <div className="h-5 w-16 bg-muted rounded-full" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (reports.length === 0) {
        return null;
    }

    const isPanel = variant === 'panel';
    const rowPadX = isPanel ? 'px-0' : 'px-4';

    const reportRows = (
        <div className="divide-y">
            {reports.map((report) => (
                <div
                    key={report.id}
                    className={`${rowPadX} flex cursor-pointer items-center justify-between gap-4 py-2.5 transition-colors hover:bg-muted/40 ${isPanel ? 'rounded-md' : ''}`}
                    onClick={() => {
                        setSelectedReport(report);
                        setReportDialogOpen(true);
                    }}
                >
                    <div className="min-w-0 flex-1">
                        <span className="text-sm font-medium truncate block">
                            {report.report_name || report.related_report_id}
                        </span>
                        <span className="text-xs text-muted-foreground">
                            {formatDate(report.created_at, 'short')}
                        </span>
                    </div>
                    <Badge
                        variant={
                            report.report_status === 'generated' ? 'default' :
                                report.report_status === 'pending' ? 'secondary' :
                                    'destructive'
                        }
                        className={`shrink-0 ${isPanel ? 'text-[10px]' : ''}`}
                    >
                        {report.report_status}
                    </Badge>
                </div>
            ))}
        </div>
    );

    return (
        <>
            {contactDetail ? (
                <div className="space-y-2">
                    <SectionHeader title="Reports" icon={FileText} />
                    <div className="rounded-xl border bg-card overflow-hidden">
                        {reportRows}
                    </div>
                </div>
            ) : isPanel ? (
                <CollapsibleSection
                    title="Reports"
                    icon={<FileText className="w-4 h-4 text-muted-foreground" />}
                    storageKey={sectionStateKey ? `${sectionStateKey}:Reports` : undefined}
                >
                    {reportRows}
                </CollapsibleSection>
            ) : (
                <div className="rounded-xl border bg-card overflow-hidden">
                    <div className="px-4 py-3 border-b flex items-center justify-between">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Reports</h3>
                        <FileText className="w-4 h-4 text-muted-foreground" />
                    </div>
                    {reportRows}
                </div>
            )}

            <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto overflow-x-hidden">
                    <DialogHeader>
                        <DialogTitle>Report Details</DialogTitle>
                        <DialogDescription>
                            {selectedReport ? (
                                selectedReport.report_name || `Report ID: ${selectedReport.related_report_id}`
                            ) : ''}
                        </DialogDescription>
                    </DialogHeader>
                    {selectedReport && (
                        <div className="space-y-6">
                            <div className="prose dark:prose-invert max-w-none space-y-6">
                                {selectedReport.report ? (
                                    <Markdown
                                        remarkPlugins={[remarkBreaks, remarkGfm]}
                                        components={markdownRenderers}
                                    >
                                        {selectedReport.report}
                                    </Markdown>
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        Report content is not available or is being generated.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <DialogFooter className="flex justify-between">
                        <Button
                            variant="destructive"
                            onClick={() => setDeleteReportDialogOpen(true)}
                            disabled={deleteReportLoading}
                        >
                            Delete Report
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setReportDialogOpen(false)}
                            disabled={deleteReportLoading}
                        >
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={deleteReportDialogOpen} onOpenChange={setDeleteReportDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Report</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this report? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteReportDialogOpen(false)}
                            disabled={deleteReportLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteReport}
                            disabled={deleteReportLoading}
                        >
                            {deleteReportLoading ? 'Deleting...' : 'Delete Report'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Reports;
